/**
 * Gradient SVG icon library — design system.
 * Unique gradient IDs per instance to avoid conflicts.
 */

import type { CSSProperties } from 'react';

export interface IconProps {
  size?: number;
  className?: string;
  style?: CSSProperties;
}

let idCounter = 0;
function uid(prefix: string) {
  return `${prefix}_${++idCounter}`;
}

/** ⚡ Молния — проект, FAB, лого */
export function IconBolt({ size = 18, className, style }: IconProps) {
  const id = uid('bolt');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} style={style}>
      <defs>
        <linearGradient id={id} x1="30%" y1="0%" x2="70%" y2="100%">
          <stop offset="0%" stopColor="#c4b5fd" />
          <stop offset="100%" stopColor="#7c3aed" />
        </linearGradient>
      </defs>
      <path d="M13 2L4.5 13H11l-1 9L20 10h-6.5L13 2z" fill={`url(#${id})`} />
    </svg>
  );
}

/** 🔥 Огонь — streak, популярность */
export function IconFire({ size = 20, className, style }: IconProps) {
  const id = uid('fire');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} style={style}>
      <defs>
        <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%" stopColor="#fbbf24" />
          <stop offset="60%" stopColor="#f97316" />
          <stop offset="100%" stopColor="#ef4444" />
        </linearGradient>
      </defs>
      <path
        d="M12 2C12 2 6 8 6 13c0 3.3 2.7 6 6 6s6-2.7 6-6C18 8 12 2 12 2zm0 14c-1.7 0-3-1.3-3-3 0-2.2 3-6 3-6s3 3.8 3 6c0 1.7-1.3 3-3 3z"
        fill={`url(#${id})`}
      />
    </svg>
  );
}

/** 💜 Сердце — реакции, лайки */
export function IconHeart({ size = 18, className, style }: IconProps) {
  const id = uid('heart');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} style={style}>
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f472b6" />
          <stop offset="100%" stopColor="#a855f7" />
        </linearGradient>
      </defs>
      <path
        d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
        fill={`url(#${id})`}
      />
    </svg>
  );
}

/** 🚀 Ракета — вирусность, шеринг */
export function IconRocket({ size = 18, className, style }: IconProps) {
  const id = uid('rocket');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} style={style}>
      <defs>
        <linearGradient id={id} x1="100%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#f0abfc" />
          <stop offset="100%" stopColor="#7c3aed" />
        </linearGradient>
      </defs>
      <path
        d="M12 2C12 2 7 6.5 7 12l-2.5 3.5 3.5-1c0 0 1.5 2.5 4 2.5s4-2.5 4-2.5l3.5 1L17 12c0-5.5-5-10-5-10zm0 8.5a2 2 0 110-4 2 2 0 010 4z"
        fill={`url(#${id})`}
      />
    </svg>
  );
}

/** ⭐ Звезда — достижения, амбассадор */
export function IconStar({ size = 18, className, style }: IconProps) {
  const id = uid('star');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} style={style}>
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#fde68a" />
          <stop offset="100%" stopColor="#f59e0b" />
        </linearGradient>
      </defs>
      <path
        d="M12 2l2.9 6.3L22 9.2l-5 4.8 1.2 6.9L12 17.6l-6.2 3.3L7 14l-5-4.8 7.1-.9L12 2z"
        fill={`url(#${id})`}
      />
    </svg>
  );
}

/** 👑 Корона — плодовитый, топ */
export function IconCrown({ size = 18, className, style }: IconProps) {
  const id = uid('crown');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} style={style}>
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#fde68a" />
          <stop offset="100%" stopColor="#d97706" />
        </linearGradient>
      </defs>
      <path d="M3 17l-1-10 5 4 5-7 5 7 5-4-1 10H3zm1 1.5h16V21H4v-2.5z" fill={`url(#${id})`} />
    </svg>
  );
}

/** 🎯 Мишень — первый проект */
export function IconTarget({ size = 18, className, style }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} style={style}>
      <circle cx="12" cy="12" r="9" stroke="#c084fc" strokeWidth="1.8" fill="none" />
      <circle cx="12" cy="12" r="5.5" stroke="#c084fc" strokeWidth="1.8" fill="none" />
      <circle cx="12" cy="12" r="2" fill="#c084fc" />
    </svg>
  );
}

/** ✨ Искры — профессионал, публикация */
export function IconSparkles({ size = 18, className, style }: IconProps) {
  const id = uid('sparkles');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} style={style}>
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#c084fc" />
          <stop offset="100%" stopColor="#818cf8" />
        </linearGradient>
      </defs>
      <path d="M12 2l1.5 6.5L20 10l-6.5 1.5L12 18l-1.5-6.5L4 10l6.5-1.5L12 2z" fill={`url(#${id})`} />
      <circle cx="19" cy="4" r="1.5" fill="#c084fc" opacity="0.5" />
      <circle cx="5" cy="18" r="1.2" fill="#818cf8" opacity="0.4" />
    </svg>
  );
}

/** 👁 Глаз — просмотры */
export function IconEye({ size = 14, className, style }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} style={style}>
      <path
        d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
      <circle cx="12" cy="12" r="3" fill="currentColor" />
    </svg>
  );
}

/** ✉ Конверт — заявки, сообщения */
export function IconMail({ size = 18, className, style }: IconProps) {
  const id = uid('mail');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} style={style}>
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#60a5fa" />
          <stop offset="100%" stopColor="#818cf8" />
        </linearGradient>
      </defs>
      <rect
        x="3"
        y="5"
        width="18"
        height="14"
        rx="2.5"
        stroke={`url(#${id})`}
        strokeWidth="1.8"
        fill="none"
      />
      <path
        d="M3 6.5l9 6.5 9-6.5"
        stroke={`url(#${id})`}
        strokeWidth="1.8"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/** 📊 График — статистика, дайджест */
export function IconChart({ size = 18, className, style }: IconProps) {
  const id = uid('chart');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} style={style}>
      <defs>
        <linearGradient id={id} x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#34d399" />
          <stop offset="100%" stopColor="#60a5fa" />
        </linearGradient>
      </defs>
      <rect x="3" y="13" width="4" height="8" rx="1.2" fill={`url(#${id})`} opacity="0.45" />
      <rect x="10" y="8" width="4" height="13" rx="1.2" fill={`url(#${id})`} opacity="0.7" />
      <rect x="17" y="3" width="4" height="18" rx="1.2" fill={`url(#${id})`} />
    </svg>
  );
}

/** 👥 Люди — рефералы */
export function IconUsers({ size = 18, className, style }: IconProps) {
  const id = uid('users');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} style={style}>
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#a78bfa" />
          <stop offset="100%" stopColor="#f472b6" />
        </linearGradient>
      </defs>
      <circle cx="9" cy="7" r="3.5" fill={`url(#${id})`} opacity="0.85" />
      <path
        d="M2 20c0-3.9 3.1-7 7-7s7 3.1 7 7"
        stroke={`url(#${id})`}
        strokeWidth="1.8"
        fill="none"
      />
      <circle cx="17" cy="6" r="2.3" fill={`url(#${id})`} opacity="0.45" />
      <path
        d="M17 11.5c2.2 0 4.5 2 4.5 5"
        stroke={`url(#${id})`}
        strokeWidth="1.3"
        opacity="0.45"
        fill="none"
      />
    </svg>
  );
}

/** 🌊 Волна — streak */
export function IconWave({ size = 18, className, style }: IconProps) {
  const id = uid('wave');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} style={style}>
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#22d3ee" />
          <stop offset="100%" stopColor="#6366f1" />
        </linearGradient>
      </defs>
      <path
        d="M2 12c2-3 4-3 6 0s4 3 6 0 4-3 6 0"
        stroke={`url(#${id})`}
        strokeWidth="2.5"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M2 17c2-3 4-3 6 0s4 3 6 0 4-3 6 0"
        stroke={`url(#${id})`}
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
        opacity="0.3"
      />
    </svg>
  );
}

/** 💎 Кристалл — премиум */
export function IconGem({ size = 18, className, style }: IconProps) {
  const id = uid('gem');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} style={style}>
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#67e8f9" />
          <stop offset="50%" stopColor="#a78bfa" />
          <stop offset="100%" stopColor="#c084fc" />
        </linearGradient>
      </defs>
      <path d="M6 3h12l4 7-10 12L2 10l4-7z" fill={`url(#${id})`} opacity="0.85" />
      <path
        d="M2 10h20M8 3l-2 7 6 12 6-12-2-7"
        stroke="rgba(255,255,255,0.3)"
        strokeWidth="0.8"
        fill="none"
      />
    </svg>
  );
}

/** 🔍 Поиск */
export function IconSearch({ size = 18, className, style }: IconProps) {
  const id = uid('search');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} style={style}>
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#60a5fa" />
          <stop offset="100%" stopColor="#a78bfa" />
        </linearGradient>
      </defs>
      <circle
        cx="10.5"
        cy="10.5"
        r="6.5"
        stroke={`url(#${id})`}
        strokeWidth="2"
        fill="none"
      />
      <path
        d="M15.5 15.5L21 21"
        stroke={`url(#${id})`}
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** 🏅 Медаль — ранний юзер */
export function IconMedal({ size = 18, className, style }: IconProps) {
  const id = uid('medal');
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} style={style}>
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#b45309" />
        </linearGradient>
      </defs>
      <path
        d="M8 2l-2 6h4l-2 3.5L12 8l4 3.5-2-3.5h4l-2-6"
        stroke={`url(#${id})`}
        strokeWidth="1.5"
        fill="none"
      />
      <circle cx="12" cy="16" r="5" fill={`url(#${id})`} />
    </svg>
  );
}

// ═══════════════════════════════════════════════════
// UI-утилитарные иконки
// ═══════════════════════════════════════════════════

/** ← Стрелка назад */
export function IconBack({ size = 18, className, style }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" className={className} style={style}>
      <path
        d="M11 4L6 9l5 5"
        stroke="var(--brand-purple-light, #a78bfa)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** ✏ Карандаш — редактирование */
export function IconPen({ size = 14, className, style }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" className={className} style={style}>
      <path d="M10 2l2 2-7 7H3V9l7-7z" fill="var(--brand-purple-light, #a78bfa)" />
    </svg>
  );
}

/** 🗑 Корзина — удаление */
export function IconTrash({ size = 14, className, style }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" className={className} style={style}>
      <path
        d="M3 4h8l-.7 7.3a1 1 0 01-1 .7H4.7a1 1 0 01-1-.7L3 4zm1-2h6m-5 2V3m4 1V3"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/** + Плюс */
export function IconPlus({ size = 22, className, style }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 22 22" fill="none" className={className} style={style}>
      <path
        d="M11 4v14M4 11h14"
        stroke="white"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** > Шеврон */
export function IconChevronRight({ size = 16, className, style }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" className={className} style={style}>
      <path
        d="M6 4l4 4-4 4"
        stroke="var(--app-text-hint, #52525b)"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** ✓ Чекмарк */
export function IconCheck({ size = 10, className, style }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 10 10" fill="none" className={className} style={style}>
      <path
        d="M2 5.5L4 7.5 8 3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** 📋 Копировать */
export function IconCopy({ size = 14, className, style }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" className={className} style={style}>
      <rect
        x="4"
        y="4"
        width="8"
        height="8"
        rx="1.5"
        stroke="var(--brand-purple-light, #a78bfa)"
        strokeWidth="1.3"
        fill="none"
      />
      <path
        d="M10 4V3c0-.6-.4-1-1-1H3c-.6 0-1 .4-1 1v6c0 .6.4 1 1 1h1"
        stroke="var(--brand-purple-light, #a78bfa)"
        strokeWidth="1.3"
        fill="none"
      />
    </svg>
  );
}

/** ↑ Поделиться */
export function IconShare({ size = 14, className, style }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" className={className} style={style}>
      <path
        d="M7 2v7M7 2L4 5M7 2l3 3M2 9v2c0 .6.4 1 1 1h8c.6 0 1-.4 1-1V9"
        stroke="var(--brand-purple-light, #a78bfa)"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** ☀ Солнце — тема */
export function IconSun({ size = 18, className, style }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" className={className} style={style}>
      <circle cx="9" cy="9" r="3.5" fill="#fbbf24" />
      <g stroke="#fbbf24" strokeWidth="1.2" strokeLinecap="round">
        <path d="M9 2.5v1.5M9 14v1.5M2.5 9H4M14 9h1.5M4.5 4.5l1 1M12.5 12.5l1 1M4.5 13.5l1-1M12.5 5.5l1-1" />
      </g>
    </svg>
  );
}
